import React, { useRef, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Dispatch } from "redux";
import { login } from "../../reducer/loginSlice";
import { AuthApis } from "../../apis/authApis";
import { AdminApis } from "../../apis/adminApi";
import { SvgElement, icontypesEnum } from "../assets/svgElement";
import { AxiosResponse } from "axios";
import {
  FiBarChart2, FiMessageSquare, FiUsers, FiList, FiHome,
  FiKey, FiCreditCard, FiSend, FiHash, FiChevronDown, FiCheck,
  FiLogOut, FiX, FiMenu,
} from "react-icons/fi";

/* ── WhatsApp icon (reused inline SVG) ─────────────────────── */
const WaIcon = ({ color = "white", size = 20 }: { color?: string; size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" className="flex-shrink-0">
    <path fill={color} d="M19.05 4.91A9.816 9.816 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21c5.46 0 9.91-4.45 9.91-9.91c0-2.65-1.03-5.14-2.9-7.01m-7.01 15.24c-1.48 0-2.93-.4-4.2-1.15l-.3-.18l-3.12.82l.83-3.04l-.2-.31a8.264 8.264 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24c2.2 0 4.27.86 5.82 2.42a8.183 8.183 0 0 1 2.41 5.83c.02 4.54-3.68 8.23-8.22 8.23m4.52-6.16c-.25-.12-1.47-.72-1.69-.81c-.23-.08-.39-.12-.56.12c-.17.25-.64.81-.78.97c-.14.17-.29.19-.54.06c-.25-.12-1.05-.39-1.99-1.23c-.74-.66-1.23-1.47-1.38-1.72c-.14-.25-.02-.38.11-.51c.11-.11.25-.29.37-.43s.17-.25.25-.41c.08-.17.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31c-.22.25-.86.85-.86 2.07c0 1.22.89 2.4 1.01 2.56c.12.17 1.75 2.67 4.23 3.74c.59.26 1.05.41 1.41.52c.59.19 1.13.16 1.56.1c.48-.07 1.47-.6 1.67-1.18c.21-.58.21-1.07.14-1.18s-.22-.16-.47-.28" />
  </svg>
);

/* ── Single nav item ────────────────────────────────────────── */
function NavItem({ to, icon, label, badge, onClick }: {
  to: string; icon: React.ReactNode; label: string;
  badge?: React.ReactNode; onClick?: () => void;
}) {
  const location = useLocation();
  const active = location.pathname.startsWith(to);
  return (
    <li className="mx-2 mb-1">
      <NavLink to={to} onClick={onClick}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${active ? "bg-white/15 text-white" : "text-white/75 hover:text-white hover:bg-white/10"}`}>
        <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">{icon}</span>
        <span className="flex-1">{label}</span>
        {badge}
      </NavLink>
    </li>
  );
}

const ComingSoonBadge = () => (
  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/15 text-white/60">Soon</span>
);

const PingDot = () => (
  <span className="relative flex h-2 w-2 flex-shrink-0">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
    <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400" />
  </span>
);

/* ── Context switcher dropdown ──────────────────────────────── */
function ContextSwitcher({ active, onChange }: {
  active: "whatsapp" | "sms";
  onChange: (v: "whatsapp" | "sms") => void;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const options = [
    { value: "whatsapp" as const, label: "WhatsApp", icon: <WaIcon size={15} /> },
    { value: "sms" as const,      label: "SMS Platform", icon: <FiMessageSquare size={14} /> },
  ];
  const current = options.find(o => o.value === active)!;

  return (
    <div ref={ref} className="relative mx-2 mb-4">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 transition-colors text-white text-sm font-semibold">
        {current.icon}
        <span className="flex-1 text-left">{current.label}</span>
        <FiChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-1.5 bg-white rounded-2xl shadow-2xl overflow-hidden z-50 border border-gray-100">
          {options.map(o => (
            <button key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold transition-colors hover:bg-gray-50 ${o.value === active ? "text-[#0071BC]" : "text-gray-700"}`}>
              <span className={o.value === active ? "text-[#0071BC]" : "text-gray-400"}>{o.icon}</span>
              <span className="flex-1 text-left">{o.label}</span>
              {o.value === active && <FiCheck size={14} className="text-[#0071BC]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main Sidebar ───────────────────────────────────────────── */
export default function Sidebar(title: any) {
  const navigate = useNavigate();
  const dispatch: Dispatch = useDispatch();
  const userLoginData = useSelector((state: any) => state.data.login.value);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const serviceType = userLoginData?.data?.service_type || userLoginData?.service_type || "all";
  const hasWA  = ["whatsapp", "all"].includes(serviceType);
  const hasSMS = ["sms", "all"].includes(serviceType);
  const hasAll = serviceType === "all";

  const location = useLocation();

  const SMS_ROUTES = [
    "/sms-dashboard", "/analytics",
    "/contacts", "/create-contact", "/edit-contact", "/import-contacts",
    "/contact-groups", "/create-contact-group", "/edit-contact-group",
    "/messages",
    "/sender-ids", "/create-sender-id",
    "/api-keys", "/create-api-key",
    "/sms-wallet", "/verify-payment",
    "/templates", "/create-template", "/edit-template",
    "/campaigns", "/create-campaign", "/edit-campaign",
  ];

  const WA_ROUTES = [
    "/mylinks", "/createlink", "/link-details",
    "/redirect-links", "/create-redirect-link",
    "/multi-links", "/create-multi-link", "/update-multi-link",
    "/mini-store", "/createproduct", "/edit-product",
    "/payment-page",
  ];

  const [activeTab, setActiveTab] = React.useState<"whatsapp" | "sms">(() => {
    if (serviceType === "sms") return "sms";
    if (serviceType === "whatsapp") return "whatsapp";
    const path = window.location.pathname;
    if (SMS_ROUTES.some(r => path === r || path.startsWith(r + "/"))) return "sms";
    if (WA_ROUTES.some(r => path === r || path.startsWith(r + "/"))) return "whatsapp";
    return "whatsapp";
  });

  // Auto-switch context when navigating — only for known routes; sub-pages keep current tab
  useEffect(() => {
    if (!hasAll) return;
    const path = location.pathname;
    if (SMS_ROUTES.some(r => path === r || path.startsWith(r + "/"))) {
      setActiveTab("sms");
    } else if (WA_ROUTES.some(r => path === r || path.startsWith(r + "/"))) {
      setActiveTab("whatsapp");
    }
    // Unknown route → keep whatever tab is active
  }, [location.pathname]);

  useEffect(() => {
    AdminApis.searchName("").then((res: AxiosResponse<any>) => {
      if (!res?.data) { dispatch(login([])); navigate("/login"); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (userLoginData?.data?.isVerified === "false") {
      AuthApis.logout("").then(() => { dispatch(login([])); navigate("/email-verify"); }).catch(() => {});
    }
  }, []);

  const logOut = () => {
    AuthApis.logout("").then((res: AxiosResponse<any>) => {
      if (res?.data) { dispatch(login([])); navigate("/login"); }
    }).catch(() => {});
  };

  const close = () => setMobileOpen(false);

  const showWA  = hasWA  && (activeTab === "whatsapp" || !hasAll);
  const showSMS = hasSMS && (activeTab === "sms"       || !hasAll);

  const SidebarContent = (
    <div className="flex flex-col h-full">

      {/* Logo */}
      <div className="px-4 pt-8 pb-6">
        <Link to="/" onClick={close}>
          <SvgElement type={icontypesEnum.BARS2} />
        </Link>
      </div>

      {/* Context switcher — only for users with both products */}
      {hasAll && (
        <ContextSwitcher active={activeTab} onChange={v => { setActiveTab(v); close(); }} />
      )}

      {/* Nav items */}
      <ul className="flex-1 overflow-y-auto pb-4 space-y-0">

        {showWA && (
          <>
            <NavItem to="/mylinks"        icon={<WaIcon size={18} />}         label="WhatsApp Links" onClick={close} />
            <NavItem to="/redirect-links" icon={<FiHash size={18} />}         label="Short Links"    onClick={close} />
            <NavItem to="/multi-links"    icon={<FiList size={18} />}         label="Multi Links"    onClick={close} />
            <NavItem to="/mini-store"     icon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" fillRule="evenodd" d="M7.435 1.25h9.13c.57 0 1.054 0 1.453.041c.426.044.82.14 1.192.37c.371.23.633.539.863.9c.215.34.432.772.687 1.282l.016.033c.01.02.019.039.027.06l1.403 3.547c.168.423.353.95.407 1.488c.055.552-.02 1.183-.453 1.73a2.753 2.753 0 0 1-1.41.945v9.604H22a.75.75 0 0 1 0 1.5H2a.75.75 0 0 1 0-1.5h1.25v-9.604a2.754 2.754 0 0 1-1.41-.944c-.432-.548-.508-1.18-.453-1.73c.054-.54.24-1.066.406-1.489l1.404-3.548a.747.747 0 0 1 .027-.06l.016-.032c.255-.51.471-.943.687-1.282c.23-.361.492-.67.863-.9c.372-.23.766-.326 1.191-.37c.4-.041.884-.041 1.454-.041M18 10.888a2.75 2.75 0 0 0 1.25.758v9.604h-4v-2.782c0-.44 0-.82-.028-1.13c-.03-.33-.096-.656-.273-.963a2.251 2.251 0 0 0-.824-.824c-.307-.177-.633-.243-.962-.273c-.312-.028-.691-.028-1.13-.028h-.065c-.44 0-.82 0-1.13.028c-.33.03-.656.096-.963.273a2.25 2.25 0 0 0-.824.824c-.177.307-.243.633-.273.962c-.028.312-.028.691-.028 1.13v2.783h-4v-9.603a2.75 2.75 0 0 0 1.25-.76a2.742 2.742 0 0 0 2 .863c.788 0 1.499-.331 2-.863a2.742 2.742 0 0 0 2 .863c.788 0 1.499-.331 2-.863a2.742 2.742 0 0 0 2 .863c.788 0 1.499-.331 2-.863M10.25 21.25h3.5V18.5c0-.481 0-.792-.022-1.027c-.02-.225-.055-.307-.079-.348a.75.75 0 0 0-.274-.274c-.04-.024-.123-.058-.348-.079A12.776 12.776 0 0 0 12 16.75c-.481 0-.792 0-1.027.022c-.225.02-.307.055-.348.079a.75.75 0 0 0-.274.274c-.024.04-.059.123-.079.348c-.021.235-.022.546-.022 1.027zM6.75 9a1.25 1.25 0 0 0 2.5 0a.75.75 0 0 1 1.5 0a1.25 1.25 0 0 0 2.5 0a.75.75 0 0 1 1.5 0a1.25 1.25 0 0 0 2.5 0a.75.75 0 0 1 1.5 0a1.25 1.25 0 0 0 2.232.773c.114-.144.17-.342.138-.652c-.032-.322-.151-.688-.308-1.086L19.42 4.517c-.268-.535-.447-.89-.613-1.15c-.16-.252-.274-.361-.386-.43c-.111-.07-.26-.123-.557-.154c-.314-.032-.72-.033-1.336-.033H7.472c-.617 0-1.023 0-1.336.033c-.297.031-.446.085-.557.154c-.112.069-.226.178-.386.43c-.167.26-.345.615-.613 1.15L3.188 8.035c-.157.398-.276.764-.308 1.086c-.031.31.024.508.138.652A1.25 1.25 0 0 0 5.25 9a.75.75 0 0 1 1.5 0" clipRule="evenodd" /></svg>} label="Mini Store" onClick={close} />
            <NavItem to="/payment-page"   icon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20"><path fill="currentColor" d="M11.67 8.537a.3.3 0 0 0-.302.296v2.212a.3.3 0 0 0 .303.296h6.663a.3.3 0 0 0 .303-.296V8.833a.3.3 0 0 0-.303-.296zm4.086-7.036c.922.044 1.585.226 2.005.612c.415.382.628.935.67 1.667v2.097a.674.674 0 0 1-.681.666a.674.674 0 0 1-.682-.666l.001-2.059c-.022-.38-.113-.616-.243-.736c-.126-.116-.51-.22-1.103-.25H2.647c-.537.02-.886.122-1.055.267c-.13.111-.228.417-.229.946l-.003 11.77c.05.514.163.857.308 1.028c.11.13.451.26.953.324h13.116c.614.012.976-.08 1.098-.203c.135-.137.233-.497.233-1.086v-2.045c0-.367.305-.666.682-.666c.376 0 .681.299.681.666v2.045c0 .9-.184 1.573-.615 2.01c-.444.45-1.15.63-2.093.61L2.54 18.495c-.897-.104-1.54-.35-1.923-.803c-.347-.41-.54-.995-.617-1.813V4.044c.002-.876.212-1.535.694-1.947c.442-.38 1.08-.565 1.927-.597zm2.578 5.704c.92 0 1.666.729 1.666 1.628v2.212c0 .899-.746 1.628-1.666 1.628h-6.663c-.92 0-1.666-.73-1.666-1.628V8.833c0-.899.746-1.628 1.666-1.628zm-4.997 1.94c-.46 0-.833.36-.833.803c0 .444.373.803.833.803c.46 0 .833-.36.833-.803c0-.444-.373-.804-.833-.804" /></svg>} label="Pay with Gupta" onClick={close} />
            {hasAll && (
              <>
                <div className="mx-2 mt-3 mb-1 px-3"><div className="h-px bg-white/10" /></div>
                <NavItem to="/automation"  icon={<FiHome size={17} />}          label="Automation"    onClick={close} badge={<ComingSoonBadge />} />
                <NavItem to="/chatrouting" icon={<FiMessageSquare size={17} />} label="Chat Routing"  onClick={close} badge={<ComingSoonBadge />} />
                <NavItem to="/formlinks"   icon={<FiList size={17} />}          label="Form Links"    onClick={close} badge={<ComingSoonBadge />} />
              </>
            )}
          </>
        )}

        {showSMS && (
          <>
            <NavItem to="/sms-dashboard"  icon={<FiHome size={17} />}       label="Dashboard"      onClick={close} />
            <NavItem to="/sms-wallet"     icon={<FiCreditCard size={17} />} label="SMS Wallet"     onClick={close} badge={<PingDot />} />
            <NavItem to="/messages"       icon={<FiSend size={17} />}       label="Send Messages"  onClick={close} />
            <NavItem to="/analytics"      icon={<FiBarChart2 size={17} />}  label="Analytics"      onClick={close} />
            <NavItem to="/contacts"       icon={<FiUsers size={17} />}      label="Contacts"       onClick={close} />
            <NavItem to="/contact-groups" icon={<FiList size={17} />}       label="Contact Groups" onClick={close} />
            <NavItem to="/sender-ids"     icon={<FiHash size={17} />}       label="Sender IDs"     onClick={close} />
            <NavItem to="/api-keys"       icon={<FiKey size={17} />}        label="API Keys"       onClick={close} />
          </>
        )}
      </ul>

      {/* Logout */}
      <div className="px-2 py-4 border-t border-white/10">
        <button onClick={logOut}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all text-sm font-medium">
          <FiLogOut size={17} />
          Log out
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <nav className="hidden md:flex md:flex-col fixed top-0 left-0 bottom-0 w-64 bg-[#0071BC] z-30 rounded-r-2xl overflow-hidden">
        {SidebarContent}
      </nav>

      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 bg-[#0071BC] sticky top-0 z-30">
        <Link to="/"><SvgElement type={icontypesEnum.BARS2} /></Link>
        <div className="flex items-center gap-3">
          <NavLink to="/subscription">
            <span className="text-xs font-bold bg-white/15 text-white px-3 py-1.5 rounded-xl">👑 Upgrade</span>
          </NavLink>
          <button onClick={() => setMobileOpen(true)} className="text-white p-1">
            <FiMenu size={22} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={close} />
          <div className="relative w-72 bg-[#0071BC] h-full overflow-y-auto flex-shrink-0">
            <button onClick={close} className="absolute top-4 right-4 text-white/60 hover:text-white">
              <FiX size={20} />
            </button>
            {SidebarContent}
          </div>
        </div>
      )}
    </>
  );
}
